import { Component } from '@theme/component';

/**
 * @typedef {object} CollectionPromoRefs
 * @property {HTMLElement} media - Promo media container
 * @property {HTMLElement} [chip] - Product preview chip
 */

/**
 * Collection grid promo: cursor-follow chip on desktop.
 * Desktop height = 1 product card + row gap + 1 card gallery.
 * While hovering, a rAF loop repositions the chip each frame so it stays
 * under a stationary cursor when the page scrolls (no scroll-event lag).
 * Mobile: CSS sticky chip; facets bar height is measured into
 * --collection-promo-facets-height for the sticky inset calc.
 * When the filter bar is folded off-screen, that value is set to 0.
 *
 * @extends {Component<CollectionPromoRefs>}
 */
class CollectionPromo extends Component {
  /** @type {number | null} */
  #rafId = null;

  /** @type {number | null} */
  #followRafId = null;

  /** @type {boolean} */
  #isDesktop = false;

  /** @type {MediaQueryList} */
  #desktopQuery = window.matchMedia('(min-width: 750px)');

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ResizeObserver | null} */
  #facetsResizeObserver = null;

  /** @type {MutationObserver | null} */
  #facetsFoldObserver = null;

  /** @type {{ x: number, y: number } | null} */
  #pointer = null;

  /** @type {number} */
  #heightRetryCount = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#isDesktop = this.#desktopQuery.matches;
    this.#desktopQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#syncMode();
  }

  /**
   * Section morph (e.g. automatic availability on load) reuses this host and
   * clears `--collection-promo-height` on the parent li. Rebind observers to
   * the post-morph product cards and recompute height immediately (CSS fallback
   * covers the brief window before cards have layout).
   */
  updatedCallback() {
    super.updatedCallback();
    if (!this.isConnected) return;
    this.#syncMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktopQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#unbindDesktopEvents();
    this.#unbindMobileFacetsHeight();
    this.#unbindResizeObserver();
    this.#stopFollowLoop();
    this.#cancelRaf();
    this.#clearDesktopHeight();
    this.#clearFacetsHeight();
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleBreakpointChange = (event) => {
    this.#isDesktop = event.matches;
    this.#syncMode();
  };

  #syncMode() {
    this.#unbindDesktopEvents();
    this.#unbindMobileFacetsHeight();
    this.#unbindResizeObserver();
    this.classList.remove('is-active');
    this.#resetChipStyles();

    if (!this.#matchesViewport()) {
      this.#clearDesktopHeight();
      this.#clearFacetsHeight();
      return;
    }

    if (this.#isDesktop) {
      this.#clearFacetsHeight();
      this.#bindDesktopEvents();
      this.#bindResizeObserver();
      this.#updateDesktopHeight();
      return;
    }

    this.#clearDesktopHeight();
    this.#bindMobileFacetsHeight();
    this.#updateFacetsHeight();
  }

  /**
   * Viewport-specific copies (`data-promo-viewport`) stay in the DOM for the
   * other breakpoint so infinite scroll can place them; skip work while hidden.
   * @returns {boolean}
   */
  #matchesViewport() {
    const gridItem = this.closest('.product-grid__item--promo');
    if (!(gridItem instanceof HTMLElement)) return true;

    const viewport = gridItem.getAttribute('data-promo-viewport');
    if (!viewport) return true;
    if (viewport === 'desktop') return this.#isDesktop;
    if (viewport === 'mobile') return !this.#isDesktop;
    return true;
  }

  #bindDesktopEvents() {
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('pointermove', this.#handlePointerMove);
    window.addEventListener('resize', this.#handleDesktopResize, { passive: true });
  }

  #unbindDesktopEvents() {
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    window.removeEventListener('resize', this.#handleDesktopResize);
    this.#stopFollowLoop();
    this.#pointer = null;
  }

  #bindMobileFacetsHeight() {
    window.addEventListener('resize', this.#handleMobileFacetsResize, { passive: true });

    const facets = this.#getFacetsControls();
    if (!facets) return;

    this.#facetsResizeObserver = new ResizeObserver(() => {
      this.#scheduleFrame(() => {
        this.#updateFacetsHeight();
      });
    });
    this.#facetsResizeObserver.observe(facets);

    // Fold uses `top`, not size — ResizeObserver will not fire. Watch direction/stuck.
    this.#facetsFoldObserver = new MutationObserver(() => {
      this.#scheduleFrame(() => {
        this.#updateFacetsHeight();
      });
    });
    this.#facetsFoldObserver.observe(facets, {
      attributes: true,
      attributeFilter: ['data-scroll-direction', 'data-stuck', 'data-fold-on-scroll'],
    });
  }

  #unbindMobileFacetsHeight() {
    window.removeEventListener('resize', this.#handleMobileFacetsResize);
    this.#facetsResizeObserver?.disconnect();
    this.#facetsResizeObserver = null;
    this.#facetsFoldObserver?.disconnect();
    this.#facetsFoldObserver = null;
  }

  #bindResizeObserver() {
    const grid = this.closest('.product-grid');
    if (!(grid instanceof HTMLElement)) return;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#scheduleFrame(() => {
        this.#updateDesktopHeight();
      });
    });

    this.#resizeObserver.observe(grid);

    const referenceCard = this.#getReferenceCard(grid);
    if (referenceCard) {
      this.#resizeObserver.observe(referenceCard);
      const gallery = referenceCard.querySelector('.card-gallery');
      if (gallery instanceof HTMLElement) {
        this.#resizeObserver.observe(gallery);
      }
    }
  }

  #unbindResizeObserver() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerEnter = (event) => {
    if (!this.#isDesktop || !this.refs.chip) return;

    this.#pointer = { x: event.clientX, y: event.clientY };
    this.classList.add('is-active');
    this.#startFollowLoop();
  };

  #handlePointerLeave = () => {
    if (!this.#isDesktop) return;

    this.classList.remove('is-active');
    this.#stopFollowLoop();
    this.#pointer = null;
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (!this.#isDesktop || !this.refs.chip || !this.refs.media) return;

    this.#pointer = { x: event.clientX, y: event.clientY };
  };

  #handleDesktopResize = () => {
    this.#scheduleFrame(() => {
      this.#updateDesktopHeight();
    });
  };

  /**
   * Repositions the chip every frame while hovered so scroll stays in sync
   * with a stationary cursor (scroll listeners alone lag one or more frames).
   */
  #startFollowLoop() {
    if (this.#followRafId !== null) return;

    const tick = () => {
      if (!this.#isDesktop || !this.#pointer || !this.classList.contains('is-active')) {
        this.#followRafId = null;
        return;
      }

      this.#positionChipDesktop(this.#pointer.x, this.#pointer.y);
      this.#followRafId = requestAnimationFrame(tick);
    };

    this.#followRafId = requestAnimationFrame(tick);
  }

  #stopFollowLoop() {
    if (this.#followRafId === null) return;
    cancelAnimationFrame(this.#followRafId);
    this.#followRafId = null;
  }

  #handleMobileFacetsResize = () => {
    this.#scheduleFrame(() => {
      this.#updateFacetsHeight();
    });
  };

  /**
   * Desktop height: one full product card + row gap + one gallery height.
   * Leaves CSS fallback in place when cards are not measurable yet.
   */
  #updateDesktopHeight() {
    if (!this.#isDesktop || !this.#matchesViewport()) {
      this.#clearDesktopHeight();
      return;
    }

    const gridItem = this.closest('.product-grid__item--promo');
    const grid = this.closest('.product-grid');
    if (!(gridItem instanceof HTMLElement) || !(grid instanceof HTMLElement)) return;

    const referenceCard = this.#getReferenceCard(grid);
    if (!referenceCard) {
      this.#retryDesktopHeight();
      return;
    }

    const gallery = referenceCard.querySelector('.card-gallery');
    if (!(gallery instanceof HTMLElement)) return;

    const cardHeight = referenceCard.getBoundingClientRect().height;
    const galleryHeight = gallery.getBoundingClientRect().height;
    if (cardHeight <= 0 || galleryHeight <= 0) {
      this.#retryDesktopHeight();
      return;
    }

    this.#heightRetryCount = 0;

    const rowGap = Number.parseFloat(getComputedStyle(grid).rowGap) || 0;
    const height = Math.round(cardHeight + rowGap + galleryHeight);
    const metaEstimate = Math.max(0, Math.round(cardHeight - galleryHeight));

    const currentHeight = Number.parseFloat(gridItem.style.getPropertyValue('--collection-promo-height'));
    if (Number.isFinite(currentHeight) && Math.abs(currentHeight - height) < 1) {
      return;
    }

    // Preserve viewport content when height changes (critical for page-1 scroll restore).
    const prevHeight = gridItem.getBoundingClientRect().height;
    const promoTop = gridItem.getBoundingClientRect().top + window.scrollY;

    if (metaEstimate > 0) {
      gridItem.style.setProperty('--collection-promo-meta-estimate', `${metaEstimate}px`);
    }

    gridItem.style.setProperty('--collection-promo-height', `${height}px`);

    const nextHeight = gridItem.getBoundingClientRect().height;
    const delta = nextHeight - prevHeight;
    if (delta !== 0 && window.scrollY > promoTop - 1) {
      window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
    }
  }

  /**
   * Retry measure a few frames while PLP restore / morph finishes laying out cards.
   * Keeps the CSS height fallback until then — never clears it on failed measure.
   */
  #retryDesktopHeight() {
    if (this.#heightRetryCount >= 12) return;
    this.#heightRetryCount += 1;
    this.#scheduleFrame(() => {
      if (this.isConnected && this.#isDesktop) this.#updateDesktopHeight();
    });
  }

  #clearDesktopHeight() {
    const gridItem = this.closest('.product-grid__item--promo');
    if (!(gridItem instanceof HTMLElement)) return;
    gridItem.style.removeProperty('--collection-promo-height');
    gridItem.style.removeProperty('--collection-promo-meta-estimate');
  }

  /**
   * Writes facets bar height onto the chip for mobile sticky inset calc.
   * When the Dstrezzed bar is folded off-screen, write 0 so the chip rises with it.
   */
  #updateFacetsHeight() {
    if (this.#isDesktop) {
      this.#clearFacetsHeight();
      return;
    }

    const { chip } = this.refs;
    if (!(chip instanceof HTMLElement)) return;

    const facets = this.#getFacetsControls();
    const isFolded = facets instanceof HTMLElement && this.#isFacetsFolded(facets);
    const height =
      isFolded || !(facets instanceof HTMLElement) ? 0 : Math.round(facets.getBoundingClientRect().height);

    chip.style.setProperty('--collection-promo-facets-height', `${height}px`);
  }

  /**
   * @param {HTMLElement} facets
   * @returns {boolean}
   */
  #isFacetsFolded(facets) {
    return (
      facets.hasAttribute('data-fold-on-scroll') &&
      facets.dataset.scrollDirection === 'down' &&
      facets.getAttribute('data-stuck') === 'true'
    );
  }

  #clearFacetsHeight() {
    const { chip } = this.refs;
    if (!(chip instanceof HTMLElement)) return;
    chip.style.removeProperty('--collection-promo-facets-height');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getFacetsControls() {
    const facets = document.querySelector(
      '.facets-dstrezzed-controls, .facets-controls-wrapper.facets--horizontal'
    );
    return facets instanceof HTMLElement ? facets : null;
  }

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement | null}
   */
  #getReferenceCard(grid) {
    const items = grid.querySelectorAll('.product-grid__item:not([data-promo-block])');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;
      if (item.querySelector('.card-gallery')) return item;
    }

    return null;
  }

  /**
   * Positions the chip near the cursor in viewport space (fixed), clamped
   * inside the media bounds. Fixed keeps it under a stationary cursor while
   * the page scrolls without waiting on a corrective transform.
   * @param {number} clientX
   * @param {number} clientY
   */
  #positionChipDesktop(clientX, clientY) {
    const { media, chip } = this.refs;
    if (!(media instanceof HTMLElement) || !(chip instanceof HTMLElement)) return;

    const mediaRect = media.getBoundingClientRect();
    const chipWidth = chip.offsetWidth;
    const chipHeight = chip.offsetHeight;
    const offset = 12;

    let x = clientX + offset;
    let y = clientY + offset;

    const minX = mediaRect.left;
    const minY = mediaRect.top;
    const maxX = Math.max(minX, mediaRect.right - chipWidth);
    const maxY = Math.max(minY, mediaRect.bottom - chipHeight);

    x = Math.min(Math.max(minX, x), maxX);
    y = Math.min(Math.max(minY, y), maxY);

    chip.style.position = 'fixed';
    chip.style.inset = 'auto';
    chip.style.left = `${x}px`;
    chip.style.top = `${y}px`;
    chip.style.transform = 'none';
  }

  #resetChipStyles() {
    const { chip } = this.refs;
    if (!(chip instanceof HTMLElement)) return;

    chip.style.transform = '';
    chip.style.position = '';
    chip.style.inset = '';
    chip.style.left = '';
    chip.style.top = '';
    chip.style.insetInlineEnd = '';
    chip.style.insetBlockStart = '';
    chip.style.insetBlockEnd = '';
  }

  /**
   * @param {() => void} callback
   */
  #scheduleFrame(callback) {
    this.#cancelRaf();
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      callback();
    });
  }

  #cancelRaf() {
    if (this.#rafId === null) return;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }
}

if (!customElements.get('collection-promo')) {
  customElements.define('collection-promo', CollectionPromo);
}
